import { sessionDataHandler } from "../sessionDataHandler";
import type { DesfechoDaConsulta, EstadoDaConversa, EtapaDaFalha } from "./diagnosticoDeEnvio";

/**
 * Avisa o backend do webchat quando algo falha no navegador, para virar alerta na
 * observabilidade (o widget não alcança o relay: a URL tem token e vive no env do
 * servidor — ver server/controllers/clientErrorController.js).
 *
 * É TELEMETRIA, com as restrições que isso impõe:
 *  - fire-and-forget: não bloqueia o envio nem lança, mesmo sem rede. Quem está
 *    tentando falar com o suporte não pode ser penalizado por um aviso nosso;
 *  - sem PII: só evento, motivo técnico, tipo e tamanho da mídia. Nome de arquivo e
 *    texto da mensagem ficam de fora de propósito.
 *
 * Desde 2026-09-01 leva também os CAMPOS DO DIAGNÓSTICO (etapa, estados, códigos, id da
 * ocorrência). Eles existem porque o motivo é texto livre e o backend o CORTA em 120 chars: o
 * código do erro ia no fim e sumia justamente quando a mensagem era longa. Estruturados, o backend
 * os monta antes do motivo e eles sobrevivem ao truncamento. São todos não-PII e revalidados lá por
 * allowlist de VALORES — aqui só se garante o formato e a ausência de campo extra.
 */

/*
 * ⚠️ TODO evento aqui precisa estar TAMBÉM na allowlist do backend
 * (`server/controllers/clientErrorController.js`), no MESMO commit. Evento só deste lado é aceito
 * com 204 e descartado — o widget "tem telemetria" e a operação não recebe nada. É a armadilha nº 1
 * desta cadeia; desde 2026-09-01 o descarte deixa rastro no log do backend.
 */
/**
 * - `send_failed`: falha ao enviar mensagem/mídia.
 * - `session_init_failed`: falha ao ABRIR o chat (conexão/token) — sem isto a operação só
 *   descobria pelo relato do cliente, porque a tela dele fica só com um aviso genérico.
 * - `send_failed_conversation_ended`: envio recusado com 400 porque a conversa já estava `closed`.
 *   Separado de `send_failed` DE PROPÓSITO: misturar os dois é o que fazia o alerta de "falha de
 *   envio" parecer um problema de rede quando era conversa encerrada.
 * - `render_crashed`: a árvore React caiu e o widget SUMIU da tela. O modo de falha mais grave que
 *   existe aqui, e até 2026-08-28 o único que não avisava ninguém (o incidente de 28/07 foi
 *   descoberto por relato humano ~1h20 depois).
 * - `reconnect_failed`: o widget tentou reconectar e DESISTIU. É a métrica que diz se a reconexão
 *   automática está resolvendo ou só maquiando o problema.
 * - `media_open_failed`: o cliente clicou no anexo e ele não abriu — nada acontece na tela dele.
 * - `csat_submit_failed`: a nota do CSAT não foi gravada. Sem isto, a pesquisa pode estar sem
 *   coletar nada e a única pista seria a Visão Geral vazia no portal.
 * - `csat_pending_failed`: o widget não conseguiu mostrar a pesquisa (sem chave, chave recusada,
 *   pesquisa ausente ou vencida quando o sinal acendeu, rede). Até 2026-09-24 esta desistência era
 *   calada, e uma pesquisa que some não deixava rastro em lugar nenhum.
 * - `uncaught_error`: exceção solta ou promise rejeitada sem tratamento no navegador. Menos grave
 *   que o crash de render (o widget continua de pé), mas é o que revela bug antes de virar crash.
 */
/*
 * Onde cada evento termina é decisão do BACKEND. Desde 2026-09-24 só a queda do webchat acorda a
 * operação no WhatsApp (`render_crashed`, `session_init_failed`, `reconnect_failed`); todos os outros
 * ficam só no log, que chega ao New Relic (ver `EVENTOS_DO_WHATSAPP` em `server/helpers/alert.js`).
 */
export type ClientErrorEvent =
    | "send_failed"
    | "session_init_failed"
    | "send_failed_conversation_ended"
    | "render_crashed"
    | "uncaught_error"
    | "reconnect_failed"
    | "media_open_failed"
    | "csat_submit_failed"
    | "csat_pending_failed"
    | "connection_denied"
    | "token_refresh_failed"
    /* ↓ degradação: o backend marca como `soLog` — conta no log, não vai pro WhatsApp. */
    | "brand_config_failed"
    /* ↓ degradação: o backend marca como `soLog` — conta no log, não vai pro WhatsApp. */
    | "promotions_load_failed"
    /* ↓ degradação: o backend marca como `soLog` — conta no log, não vai pro WhatsApp. */
    | "session_storage_corrupted"
    /* ↓ degradação: o backend marca como `soLog` — conta no log, não vai pro WhatsApp. */
    | "revalidate_failed";
export interface ClientErrorDetail {
    /** Mensagem técnica do erro (ex.: a do SDK do Twilio). Nunca conteúdo do cliente. */
    reason?: string;
    /**
     * Impressão OPACA do erro (base36 curto). Identifica o bug sem carregar o texto dele.
     * Sem ela, dois bugs distintos dividem a chave de throttle do backend e o segundo é engolido.
     */
    errorFingerprint?: string;
    /**
     * De onde nasceu a tentativa de reconexão. Enumerado e revalidado por allowlist no backend.
     *
     * Existe porque todo `reconnect_failed` saía com a mesma frase, e a operação não conseguia
     * separar apostador com problema de rede do ruído que o próprio widget provoca (o `shutdown()`
     * deliberado ao clicar em "Iniciar nova conversa").
     */
    origemDaReconexao?: "desligamento_proprio" | "conexao_perdida" | "volta_de_aba";
    /** Tipos dos anexos (só MIME — NUNCA o nome do arquivo, que costuma carregar PII). */
    mediaType?: string;
    /** Soma dos tamanhos, em bytes. */
    mediaSize?: number;
    /** Quantos anexos iam na mensagem. Com 1 só, a falha pode ter sido no 2º — precisa aparecer. */
    mediaCount?: number;
    /** Id da ocorrência — é ele que responde "dois alertas são o mesmo evento?". */
    reportId?: string;
    failureStage?: EtapaDaFalha;
    /** O que o widget ACHAVA vs. o que o Twilio respondeu — a divergência é o diagnóstico. */
    conversationStateLocal?: EstadoDaConversa;
    conversationStateRemote?: EstadoDaConversa;
    /** "ok" com estado, ou por que não deu pra saber. Nunca colapsar isto em "ativa". */
    lookupOutcome?: DesfechoDaConsulta;
    humanMode?: boolean;
    connectionState?: string;
    errorCode?: string;
    errorStatus?: string;
}

const MAX_REASON_CHARS = 200;

export function reportClientError(event: ClientErrorEvent, detail: ClientErrorDetail = {}): void {
    /*
     * "Não lança" é o contrato, e agora é crítico: desde que a telemetria entrou no caminho de
     * ABRIR o chat (falha de `initSession`), uma exceção daqui transformaria uma falha TRATADA em
     * falha não tratada — o apostador voltaria a ficar preso no spinner. Mesmo padrão
     * best-effort-blindado de `alert.js`/`heartbeat.js`/`notifyWelcome.js` no server.
     */
    let endpoint: string | undefined;
    try {
        endpoint = sessionDataHandler.getEndpoint();
    } catch {
        return;
    }
    if (!endpoint) {
        return;
    }

    // Allowlist explícita: o objeto enviado é montado campo a campo para nenhum dado
    // extra escapar por engano se `detail` crescer no futuro.
    const body: Record<string, unknown> = { event };
    if (typeof detail.reason === "string" && detail.reason) {
        body.reason = detail.reason.slice(0, MAX_REASON_CHARS);
    }
    if (typeof detail.mediaType === "string" && detail.mediaType) {
        body.mediaType = detail.mediaType;
    }
    if (typeof detail.mediaSize === "number" && Number.isFinite(detail.mediaSize)) {
        body.mediaSize = detail.mediaSize;
    }
    if (typeof detail.mediaCount === "number" && Number.isFinite(detail.mediaCount)) {
        body.mediaCount = detail.mediaCount;
    }
    /*
     * Campo a campo, e só quando informado: campo vazio no alerta é ruído, e "ausente" tem
     * significado próprio (o backend omite o que não recebe, em vez de exibir vazio).
     */
    const texto: (keyof ClientErrorDetail)[] = [
        "reportId",
        "failureStage",
        "conversationStateLocal",
        "conversationStateRemote",
        "lookupOutcome",
        "connectionState",
        "errorCode",
        "errorStatus",
        /*
         * Discrimina DOIS bugs distintos que se descrevem com o mesmo motivo. Sem ela, a chave de
         * throttle do backend vira `evento + motivo` e o segundo bug da janela é engolido em
         * silêncio — e a linha de log do ECS fica indistinguível da do primeiro.
         */
        "errorFingerprint",
        /*
         * 🔴 Esta lista é o que DE FATO é enviado. Acrescentar o campo só na interface acima faz
         * ele morrer aqui, em silêncio, antes do POST — achado da revisão do plano.
         */
        "origemDaReconexao"
    ];
    texto.forEach((campo) => {
        const valor = detail[campo];
        if (typeof valor === "string" && valor) {
            body[campo] = valor;
        }
    });
    if (typeof detail.humanMode === "boolean") {
        body.humanMode = detail.humanMode;
    }

    void fetch(`${endpoint}/clientError`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        /*
         * ⚠️ SOBREVIVE À PÁGINA MORRENDO. Sem `keepalive` o navegador CANCELA esta requisição quando
         * a aba fecha ou navega — e o erro que mais importa (cliente que some depois da falha) é
         * exatamente esse. A classe de erro mais grave seria a que menos chega.
         */
        keepalive: true
    }).catch(() => undefined);
}
